// 共通 API リクエスト基盤。
// - request(): fetch ラッパ。CSRF トークン自動付与、403/401/429 統一処理
// - fetchCsrfToken(): /api/auth/csrf を叩いてトークン取得
// - setCsrfToken(): ログイン後など、サーバから返ったトークンをセット
// 2026-05-15 リファクタで src/api.ts から分離。
//
// 全 API モジュール（auth/users/masters/transactions/operations）はここを通って通信する。

export const BASE = '/api'

let csrfToken: string | null = null

export async function fetchCsrfToken() {
  const res = await fetch(`${BASE}/auth/csrf`, { credentials: 'include' })
  const data = await res.json()
  csrfToken = data.csrfToken
  return csrfToken
}

export function setCsrfToken(token: string) {
  csrfToken = token
}

/** 現在保持している CSRF トークンを取得（attachments の multipart アップロード等で必要） */
export function getCsrfToken(): string | null {
  return csrfToken
}

export async function request<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  // 書き込み系リクエストにCSRFトークンを付与
  const method = (options.method || 'GET').toUpperCase()
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
    if (!csrfToken) await fetchCsrfToken()
    headers['X-CSRF-Token'] = csrfToken!
  }

  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    cache: 'no-store', // 古いキャッシュ表示を防ぐ（特にドラッグ&ドロップ後の cells 同期）
    ...options,
    headers,
  })

  // ZER-333: session rotation で新 CSRF token が返されたら保存
  //   middleware が書き込みリクエスト成功時に session_id を rotate し、
  //   新 csrf cookie + X-New-Csrf-Token ヘッダで通知する。
  //   フロント側はモジュール変数 csrfToken を更新して次回リクエストに使う。
  const newCsrf = res.headers.get('X-New-Csrf-Token')
  if (newCsrf) {
    setCsrfToken(newCsrf)
  }

  // CSRFエラー → トークン再取得してリトライ
  if (res.status === 403) {
    const data = await res.json()
    if (data.error?.includes('CSRF')) {
      await fetchCsrfToken()
      headers['X-CSRF-Token'] = csrfToken!
      const retryRes = await fetch(`${BASE}${path}`, {
        credentials: 'include',
        ...options,
        headers,
      })
      const retryData = await retryRes.json()
      if (!retryRes.ok) throw new Error(retryData.error || 'APIエラー')
      return retryData
    }
    throw new Error(data.error || '権限がありません')
  }

  if (res.status === 401) {
    // サーバーから返されたエラーメッセージを優先して表示（原因特定容易化）
    let msg = '認証が必要です'
    try {
      const data = await res.json()
      if (data?.error) msg = data.error
    } catch { /* ignore json parse errors */ }
    throw new Error(msg)
  }

  if (res.status === 429) {
    const data = await res.json()
    throw new Error(data.error || '試行回数が上限に達しました')
  }

  // レスポンスボディをテキストで読んでからJSONパース試行
  const text = await res.text()
  let data: any = {}
  try {
    data = text ? JSON.parse(text) : {}
  } catch {
    // HTMLレスポンス（500エラー等）の場合
    throw new Error(`サーバーエラー (${res.status}): ${path}`)
  }
  if (!res.ok) {
    // 409 Conflict: 承認が必要等、レスポンス全体をエラーに埋め込む
    const err: any = new Error(data.error || `APIエラー (${res.status})`)
    err.status = res.status
    err.data = data
    throw err
  }
  return data
}

/** ApiError: fetch由来のエラーに追加情報を持たせる */
export interface ApiError extends Error {
  status?: number
  data?: any
}
